import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { MobileFilterSheet, type OpcaoDeFiltro } from "./MobileFilterSheet";

type Props = {
  statusOpcoes: OpcaoDeFiltro[];
  statusSelecionados: string[];
  onStatusChange: (valores: string[]) => void;

  retrabalhoAtivo: boolean;
  onRetrabalhoChange: (ativo: boolean) => void;

  estagioOpcoes: OpcaoDeFiltro[];
  estagiosSelecionados: string[];
  onEstagiosChange: (valores: string[]) => void;

  dentistas: string[];
  dentista: string;
  onDentistaChange: (v: string) => void;

  clinicas: string[];
  clinica: string;
  onClinicaChange: (v: string) => void;
};

type Chave = "status" | "retrabalho" | "estagio" | "dentista" | "clinica";

const RETRABALHO_ABERTO = "aberto";
const comoOpcoes = (nomes: string[]): OpcaoDeFiltro[] => nomes.map((n) => ({ valor: n, rotulo: n }));

/**
 * Filtros do celular: Status, Retrabalho, Estágio, Dentista e Clínica, cada um com o próprio
 * contador e a escolha numa folha inferior.
 *
 * Substitui o botão genérico "Filtros" que abria um painel: no celular aquele painel empilhava
 * dezenas de chips e empurrava o conteúdo pra baixo da dobra. Dentista e clínica eram dois selects
 * de 44px numa linha só deles; aqui viram mais dois botões e o topo da página encolhe.
 *
 * O rótulo do filtro fica SEMPRE visível — "Status 2", nunca "Em produção" — porque trocar o nome
 * pelo primeiro valor escolhido esconde que existem outros marcados.
 */
export function MobileProductionFilters({
  statusOpcoes,
  statusSelecionados,
  onStatusChange,
  retrabalhoAtivo,
  onRetrabalhoChange,
  estagioOpcoes,
  estagiosSelecionados,
  onEstagiosChange,
  dentistas,
  dentista,
  onDentistaChange,
  clinicas,
  clinica,
  onClinicaChange,
}: Props) {
  const [aberta, setAberta] = useState<Chave | null>(null);

  const chips: { chave: Chave; rotulo: string; quantos: number }[] = [
    { chave: "status", rotulo: "Status", quantos: statusSelecionados.length },
    { chave: "retrabalho", rotulo: "Retrabalho", quantos: retrabalhoAtivo ? 1 : 0 },
    { chave: "estagio", rotulo: "Estágio", quantos: estagiosSelecionados.length },
    { chave: "dentista", rotulo: "Dentista", quantos: dentista ? 1 : 0 },
    { chave: "clinica", rotulo: "Clínica", quantos: clinica ? 1 : 0 },
  ];

  return (
    <div className="mb-3 md:hidden">
      {/* Largura pelo texto e `flex-auto` pra preencher: três numa linha, dois na outra, sem rolagem. */}
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filtros">
        {chips.map(({ chave, rotulo, quantos }) => (
          <button
            key={chave}
            type="button"
            onClick={() => setAberta(chave)}
            aria-haspopup="dialog"
            className={cn(
              "inline-flex h-10 flex-auto items-center justify-center gap-1 whitespace-nowrap rounded-md border px-3 text-[13px] font-semibold transition-colors",
              quantos > 0
                ? "border-brand-200 bg-brand-50 text-brand-700 dark:border-primary/30 dark:bg-primary/15 dark:text-primary"
                : "border-zinc-200 bg-white text-zinc-700 dark:border-border dark:bg-card dark:text-foreground",
            )}
          >
            {rotulo}
            {quantos > 0 && (
              <span className="inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-600 px-1 text-[10px] font-bold text-white">
                {quantos}
              </span>
            )}
            <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
          </button>
        ))}
      </div>

      <MobileFilterSheet
        aberto={aberta === "status"}
        titulo="Status"
        opcoes={statusOpcoes}
        selecionados={statusSelecionados}
        onFechar={() => setAberta(null)}
        onAplicar={onStatusChange}
      />

      {/* Retrabalho é binário no estado da página (`reworkOnly`). Aparece como opção única pra não
          inventar regra nova de filtragem só por causa da forma do controle. */}
      <MobileFilterSheet
        aberto={aberta === "retrabalho"}
        titulo="Retrabalho"
        opcoes={[{ valor: RETRABALHO_ABERTO, rotulo: "Com retrabalho aberto", cor: "#ef4444" }]}
        selecionados={retrabalhoAtivo ? [RETRABALHO_ABERTO] : []}
        onFechar={() => setAberta(null)}
        onAplicar={(v) => onRetrabalhoChange(v.includes(RETRABALHO_ABERTO))}
      />

      <MobileFilterSheet
        aberto={aberta === "estagio"}
        titulo="Estágio"
        opcoes={estagioOpcoes}
        selecionados={estagiosSelecionados}
        comBusca={estagioOpcoes.length > 8}
        onFechar={() => setAberta(null)}
        onAplicar={onEstagiosChange}
      />

      <MobileFilterSheet
        aberto={aberta === "dentista"}
        titulo="Dentista"
        opcoes={comoOpcoes(dentistas)}
        selecionados={dentista ? [dentista] : []}
        unico
        comBusca={dentistas.length > 8}
        onFechar={() => setAberta(null)}
        onAplicar={(v) => onDentistaChange(v[0] ?? "")}
      />

      <MobileFilterSheet
        aberto={aberta === "clinica"}
        titulo="Clínica"
        opcoes={comoOpcoes(clinicas)}
        selecionados={clinica ? [clinica] : []}
        unico
        comBusca={clinicas.length > 8}
        onFechar={() => setAberta(null)}
        onAplicar={(v) => onClinicaChange(v[0] ?? "")}
      />
    </div>
  );
}
